import { useCallback, useEffect, useRef, useState } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import {
  BATCH_PAGE_SIZE,
  BATCH_SELECTOR_PAGE_SIZE,
} from '../constants/partner-data-gateway.constant';
import { FileProcessingStatus } from '../domain/enums';
import { BatchesPage, BatchHeatmapCell, BatchTotals } from '../domain/types';
import { isTerminalStatus } from '../utils/ingestion-status.utils';
import { isTabVisible } from '../utils/tab-visibility.util';
import { useAsyncResource } from './use-async-resource';

/**
 * Batches move faster than the file as a whole — an operator watching the
 * heatmap expects cells to flip within a few seconds of the worker
 * finishing them.
 */
const DEFAULT_POLL_INTERVAL_MS = 3000;

export const EMPTY_BATCH_TOTALS: BatchTotals = {
  total: 0,
  pending: 0,
  inProgress: 0,
  completed: 0,
  failed: 0,
  skipped: 0,
};

const EMPTY_BATCHES_PAGE: BatchesPage = {
  items: [],
  pageInfo: { hasNextPage: false, nextCursor: null },
  totals: EMPTY_BATCH_TOTALS,
};

export interface UseIngestionBatchesParams {
  /**
   * Status of the parent ingestion. Batch rows carry their own lifecycle, so
   * the only reliable signal that no further batch will ever change is the
   * parent file reaching a terminal status.
   */
  parentStatus: FileProcessingStatus;
  limit?: number;
  /** Set to `0` to disable polling entirely (e.g. in tests). */
  pollIntervalMs?: number;
}

export interface UseIngestionBatchesReturn {
  batches: BatchHeatmapCell[];
  /** Whole-file rollup, not just the current page. */
  totals: BatchTotals;
  isLoading: boolean;
  hasError: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  currentPage: number;
  goToNext: () => void;
  goToPrevious: () => void;
  refetch: () => void;
}

/**
 * Fetches a page of parsing batches for one ingestion, together with the
 * whole-file `totals` rollup the heatmap's summary bar renders.
 *
 * Cursor history is tracked here (as in `use-ingestions`) so callers can page
 * in both directions without knowing how cursors are transported; PDGS
 * exposes no total count, so forward paging is driven by
 * `pageInfo.hasNextPage`.
 */
export const useIngestionBatches = (
  eventId: string,
  {
    parentStatus,
    limit = BATCH_PAGE_SIZE,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  }: UseIngestionBatchesParams,
): UseIngestionBatchesReturn => {
  const client = usePartnerDataClient();

  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>(
    [undefined],
  );
  const [cursorIndex, setCursorIndex] = useState(0);
  const cursor = cursorHistory[cursorIndex];

  const fetchBatches = useCallback(
    () => client.listBatches(eventId, { cursor, limit }),
    [client, eventId, cursor, limit],
  );

  const { data, isLoading, hasError, refetch } = useAsyncResource(
    fetchBatches,
    EMPTY_BATCHES_PAGE,
    // A transient poll failure must not blank the heatmap — the last good
    // snapshot stays on screen and the tab shows a stale-data warning.
    { keepPreviousDataOnError: true },
  );

  // A different ingestion has different cursors, so the stack cannot carry
  // over.
  useEffect(() => {
    setCursorHistory([undefined]);
    setCursorIndex(0);
  }, [eventId, limit]);

  // Poll via a ref so the interval always calls the latest `refetch` without
  // being torn down and recreated on every render.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const shouldPoll = !isTerminalStatus(parentStatus);

  useEffect(() => {
    if (!shouldPoll || pollIntervalMs <= 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (isTabVisible()) {
        refetchRef.current();
      }
    }, pollIntervalMs);

    return (): void => clearInterval(intervalId);
  }, [shouldPoll, pollIntervalMs]);

  const goToNext = (): void => {
    const { hasNextPage, nextCursor } = data.pageInfo;

    if (!hasNextPage || !nextCursor) {
      return;
    }

    setCursorHistory(previous => [
      ...previous.slice(0, cursorIndex + 1),
      nextCursor,
    ]);
    setCursorIndex(previous => previous + 1);
  };

  const goToPrevious = (): void => {
    if (cursorIndex === 0) {
      return;
    }

    setCursorIndex(previous => previous - 1);
  };

  return {
    batches: data.items,
    totals: data.totals,
    isLoading,
    hasError,
    hasNext: data.pageInfo.hasNextPage,
    hasPrevious: cursorIndex > 0,
    currentPage: cursorIndex + 1,
    goToNext,
    goToPrevious,
    refetch,
  };
};

export interface UseIngestionBatchSelectorParams {
  limit?: number;
}

export interface UseIngestionBatchSelectorReturn {
  batches: BatchHeatmapCell[];
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
}

/**
 * The first page of a file's parsing batches.
 *
 * Consumers that only need to *choose* a batch (the Records tab's selector)
 * use this rather than paging through them: `pdgIngestionRecords` requires a
 * `batchId`, so the choice has to exist before any record can be fetched.
 */
export const useIngestionBatchSelector = (
  eventId: string,
  { limit = BATCH_SELECTOR_PAGE_SIZE }: UseIngestionBatchSelectorParams = {},
): UseIngestionBatchSelectorReturn => {
  const client = usePartnerDataClient();

  const fetchBatches = useCallback(
    () => client.listBatches(eventId, { limit }),
    [client, eventId, limit],
  );

  const { data, isLoading, hasError, refetch } = useAsyncResource<BatchesPage>(
    fetchBatches,
    EMPTY_BATCHES_PAGE,
  );

  return { batches: data.items, isLoading, hasError, refetch };
};
